/**
 * React context provider for the typed API client.
 * Creates an API client bound to the MSAL instance and provides it to all hooks.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { createApiClient } from "@/lib/api-client";

type ApiClient = ReturnType<typeof createApiClient>;

const ApiContext = createContext<ApiClient | null>(null);

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

interface ApiProviderProps {
  readonly children: ReactNode;
}

export function ApiProvider({ children }: ApiProviderProps) {
  const { instance } = useMsal();

  const apiClient = useMemo(
    () => createApiClient({ msalInstance: instance, baseUrl: API_BASE_URL }),
    [instance],
  );

  return <ApiContext.Provider value={apiClient}>{children}</ApiContext.Provider>;
}

/**
 * Access the API client from any component.
 * Must be used within an ApiProvider.
 */
export function useApiClient(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) {
    throw new Error("useApiClient must be used within an ApiProvider");
  }
  return client;
}
